import { createContext, useEffect, useState, useMemo, useContext, useCallback } from 'react';
import { useGlobal } from './GlobalContext';

export const SUPPORTED_CURRENCIES = ['USD', 'ARS'];

const defaultFiltersProviderValues = {
  filters: {
    scales: 'Todas las escalas',
    cabinClass: 'Y',
    currency: 'USD',
    minPrice: 0,
    maxPrice: undefined,
    airlines: ['Todas las aerolíneas'],
    direct: false,
    baggageFilters: {
      withBaggage: false,
      withoutBaggage: false,
    },
    baggageCount: 0,
  },
  availableCurrencies: SUPPORTED_CURRENCIES,
  setFilters: () => null,
  handleFilters: () => null,
  handleResetFilters: () => null,
  resetAllFlightsFilters: () => null,
  data: [],
  setData: () => null,
  filteredData: [],
  isLoading: true,
  setIsLoading: () => null,
  error: null,
  setError: () => null,
  minMaxDataRangePrice: [],
  getAirlines: [],
  toggleBooleanFilter: () => null,
};

export const cabinClassDic = {
  economy: 'Y',
  premiumEconomy: 'W',
  business: 'C',
  firstClass: 'F',
};

const FlightsFiltersContext = createContext(defaultFiltersProviderValues);

const FlightsFiltersProvider = ({ children }) => {
  const [filters, setFilters] = useState(defaultFiltersProviderValues.filters);
  const [data, setData] = useState(defaultFiltersProviderValues.data);
  const [isLoading, setIsLoading] = useState(defaultFiltersProviderValues.isLoading);
  const [error, setError] = useState(defaultFiltersProviderValues.error);
  const { onSearchCount } = useGlobal();
  const [availableCurrencies, setAvailableCurrencies] = useState(SUPPORTED_CURRENCIES);

  useEffect(() => {
    if (!data?.length) return;

    const uniqueCurrencies = new Set(['USD', 'ARS']);
    data.forEach(({ currency }) => {
      if (currency) uniqueCurrencies.add(currency);
    });

    setAvailableCurrencies(Array.from(uniqueCurrencies));
  }, [data]);

  const filterByScales = useCallback(
    (card) => {
      if (filters.scales === 'Todas las escalas' && !filters.direct) {
        return true;
      }

      const flightsBySlug = card.flight.reduce((acc, flight) => {
        if (!flight.slug) {
          const key = `direct-${flight.originDestinationReferences}`;
          if (!acc[key]) acc[key] = [];
          acc[key].push(flight);
        } else {
          if (!acc[flight.slug]) acc[flight.slug] = [];
          acc[flight.slug].push(flight);
        }
        return acc;
      }, {});

      const scalesPerRoute = Object.values(flightsBySlug).map((flights) => {
        if (!flights[0].slug) return 0;
        return flights.length - 1;
      });

      const isValidScale = scalesPerRoute.some((stops) => {
        if (filters.direct) return stops === 0;

        switch (filters.scales) {
          case 'Vuelo directo':
            return stops === 0;
          case '1 Escala':
            return stops === 1;
          case '2 o más escalas':
            return stops >= 2;
          default:
            return true;
        }
      });

      return isValidScale;
    },
    [filters.scales, filters.direct]
  );

  const filterByAirlines = useCallback(
    (card) => {
      if (filters.airlines.includes('Todas las aerolíneas')) return true;

      const flightsByReference = card.flight.reduce((acc, flight) => {
        const ref = flight.originDestinationReferences;
        if (!acc[ref]) acc[ref] = [];
        acc[ref].push(flight);
        return acc;
      }, {});

      const hasValidSegments = Object.values(flightsByReference).some((flights) =>
        flights.some((flight) => filters.airlines.includes(flight.company))
      );

      return hasValidSegments;
    },
    [filters.airlines]
  );

  const baggageCount = useMemo(() => {
    if (!data || !data.length) return 0;
    return data.reduce((count, card) => {
      const hasBaggage = card.flight.some((flight) => flight.baggage > 0);
      return hasBaggage ? count + 1 : count;
    }, 0);
  }, [data]);

  const filterByBaggage = useCallback(
    (card) => {
      const { withBaggage, withoutBaggage } = filters.baggageFilters;

      if (!withBaggage && !withoutBaggage) return true;

      const hasBaggage = card.flight.some((flight) => flight.baggage > 0);

      if (withBaggage) return hasBaggage;
      if (withoutBaggage) return !hasBaggage;

      return true;
    },
    [filters.baggageFilters]
  );

  const filteredData = useMemo(() => {
    if (!data || !data.length) return [];
    return data
      .filter((card) => {
        const passesScaleFilter = filterByScales(card);
        const passesAirlineFilter = filterByAirlines(card);
        const passesBaggageFilter = filterByBaggage(card);
        return passesScaleFilter && passesAirlineFilter && passesBaggageFilter;
      })
      .filter((item) => item.total >= filters.minPrice && item.total <= filters.maxPrice);
  }, [data, filters, filterByScales, filterByAirlines, filterByBaggage]);

  const handleFilters = useCallback((key, value) => {
    setFilters((prev) => {
      let newFilters = { ...prev };

      if (key === 'baggageFilters') {
        newFilters.baggageFilters = value;
      } else {
        newFilters = { ...prev, [key]: value };

        if (['cabinClass', 'currency'].includes(key)) {
          newFilters = {
            ...newFilters,
            airlines: defaultFiltersProviderValues.filters.airlines,
          };
        }
      }

      return newFilters;
    });
  }, []);

  const minMaxDataRangePrice = useMemo(() => {
    if (!data?.length) {
      return [
        defaultFiltersProviderValues.filters.minPrice,
        defaultFiltersProviderValues.filters.maxPrice,
      ];
    }
    const totals = data.map((item) => item.total);
    const maxPrice = Math.max(...totals);
    const minPrice = Math.min(...totals);
    return [minPrice, maxPrice];
  }, [data]);

  const getAirlines = useMemo(() => {
    if (!data || !data.length) return ['Todas las aerolíneas'];

    const uniqueAirlines = new Set(
      data.flatMap((item) => item.flight.map((flight) => flight.company))
    );

    return ['Todas las aerolíneas', ...Array.from(uniqueAirlines)];
  }, [data]);

  const handleResetFilters = useCallback(() => {
    setFilters({
      ...defaultFiltersProviderValues.filters,
      minPrice: minMaxDataRangePrice[0],
      maxPrice: minMaxDataRangePrice[1],
    });
  }, [minMaxDataRangePrice]);

  const resetAllFlightsFilters = useCallback(() => {
    setFilters(defaultFiltersProviderValues.filters);
    setData([]);
    setIsLoading(false);
    setError(null);
    setAvailableCurrencies(SUPPORTED_CURRENCIES);
  }, []);

  const toggleBooleanFilter = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value !== undefined ? value : !prev[key],
    }));
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      setFilters((prev) => {
        if (
          prev.minPrice === minMaxDataRangePrice[0] &&
          prev.maxPrice === minMaxDataRangePrice[1]
        ) {
          return prev;
        }
        return {
          ...prev,
          minPrice: minMaxDataRangePrice[0],
          maxPrice: minMaxDataRangePrice[1],
        };
      });
    }

    return () => {
      isMounted = false;
    };
  }, [minMaxDataRangePrice]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      setFilters((prev) => {
        if (prev.baggageCount === baggageCount) {
          return prev;
        }
        return {
          ...prev,
          baggageCount,
        };
      });
    }

    return () => {
      isMounted = false;
    };
  }, [baggageCount]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted && !!onSearchCount) {
      handleResetFilters();
    }

    return () => {
      isMounted = false;
    };
  }, [onSearchCount]);

  useEffect(() => {
    if (onSearchCount === 0) {
      resetAllFlightsFilters();
    }
  }, [onSearchCount, resetAllFlightsFilters]);

  const values = {
    handleFilters,
    handleResetFilters,
    resetAllFlightsFilters,
    filters,
    setFilters,
    setData,
    isLoading,
    setIsLoading,
    error,
    setError,
    filteredData,
    minMaxDataRangePrice,
    getAirlines,
    toggleBooleanFilter,
    availableCurrencies,
  };

  return <FlightsFiltersContext.Provider value={values}>{children}</FlightsFiltersContext.Provider>;
};

const useFlightsFilters = () => useContext(FlightsFiltersContext);

export { FlightsFiltersContext, FlightsFiltersProvider, useFlightsFilters };
